import React, { useState } from "react";
import { getIdWithToken } from "../../../../utils/storage/Token";
import {
    useFetchApiKeys,
    CreateApiKey,
    RevokeApiKey,
    DeleteApiKey,
    IApiKeyInfo,
    IApiKeyCreated,
} from "../../../../service/ApiKeyService";
import FCSpinner from "../../../FCObjets/FCSpinner";
import FCAlertMsgErrorAPI from "../../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import "./ContentApiKeys.scss";

// ─── Types & données MCP ─────────────────────────────────────────────────────

type McpCapabilityType = "resource" | "tool" | "prompt";

interface IMcpParam {
    name: string;
    desc: string;
    required?: boolean;
}

interface IMcpCapability {
    name: string;
    description: string;
    params: IMcpParam[];
}

interface IMcpSection {
    type: McpCapabilityType;
    label: string;
    icon: string;
    items: IMcpCapability[];
}

const MCP_SECTIONS: IMcpSection[] = [
    {
        type: "resource",
        label: "Ressources (lecture)",
        icon: "database",
        items: [
            {
                name: "billings",
                description: "Liste des factures, filtrables par état ou année.",
                params: [
                    { name: "include_canceled", desc: "Inclure les annulées (défaut : false)", required: false },
                    { name: "state_filter", desc: "Filtrer par état numérique (0–7)", required: false },
                ],
            },
            {
                name: "contracts",
                description: "Contrats actifs du freelance avec numéro, client et TJM.",
                params: [
                    { name: "include_ended", desc: "Inclure les terminés/annulés (défaut : false)", required: false },
                ],
            },
            {
                name: "revenue",
                description: "Chiffre d'affaires réalisé et en cours.",
                params: [{ name: "year", desc: "Filtrer par année, ex : 2025", required: false }],
            },
        ],
    },
    {
        type: "tool",
        label: "Outils (actions)",
        icon: "build",
        items: [
            {
                name: "list_contracts",
                description:
                    "Lister les contrats actifs du freelance avec leur id interne, numéro de contrat, client et TJM.",
                params: [
                    {
                        name: "include_ended",
                        desc: "Inclure les contrats terminés/annulés (défaut : false)",
                        required: false,
                    },
                ],
            },
            {
                name: "list_invoices",
                description: "Lister les factures du freelance.",
                params: [
                    { name: "include_canceled", desc: "Inclure les annulées (défaut : false)", required: false },
                    { name: "state_filter", desc: "Filtrer par état (0=Init … 7=Payée)", required: false },
                ],
            },
            {
                name: "get_revenue",
                description: "Chiffre d'affaires réalisé et en cours, par année.",
                params: [{ name: "year", desc: "Année, ex : 2025 (optionnel)", required: false }],
            },
            {
                name: "create_invoice",
                description: "Créer une facture brouillon pour un contrat existant.",
                params: [
                    { name: "id_business_deal", desc: "ID interne du contrat (via list_contracts)", required: true },
                    { name: "number", desc: "Numéro de facture (ex : FAC-2025-001)", required: true },
                    { name: "begin / end", desc: "Dates de la période (ISO 8601)", required: true },
                    { name: "items[]", desc: "Lignes : label, quantité, prix HT, TVA%", required: true },
                ],
            },
        ],
    },
    {
        type: "prompt",
        label: "Prompts",
        icon: "auto_awesome",
        items: [
            {
                name: "monthly_summary",
                description: "Génère un résumé mensuel du chiffre d'affaires et des dépenses.",
                params: [
                    { name: "month", desc: "Mois (1–12)", required: true },
                    { name: "year", desc: "Année, ex : 2025", required: true },
                ],
            },
        ],
    },
];

const ContentApiKeys: React.FC = () => {
    const id_user = getIdWithToken();
    const [forceUpdated, setForceUpdated] = useState(0);
    const { data: keys, loading, error } = useFetchApiKeys(id_user, forceUpdated);

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newKeyName, setNewKeyName] = useState("");
    const [creatingKey, setCreatingKey] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const [createdKey, setCreatedKey] = useState<IApiKeyCreated | null>(null);
    const [copiedRawKey, setCopiedRawKey] = useState(false);

    const [actionError, setActionError] = useState<string | null>(null);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newKeyName.trim()) return;
        setCreatingKey(true);
        setCreateError(null);
        try {
            const res = await CreateApiKey(id_user, newKeyName.trim());
            setCreatedKey(res.data);
            setShowCreateForm(false);
            setNewKeyName("");
            setForceUpdated((v) => v + 1);
        } catch (err: any) {
            setCreateError(err?.response?.data?.error ?? "Erreur lors de la création de la clé.");
        } finally {
            setCreatingKey(false);
        }
    };

    const handleRevoke = async (key: IApiKeyInfo) => {
        if (!window.confirm(`Révoquer la clé "${key.name}" ? Elle ne pourra plus être utilisée.`)) return;
        setActionError(null);
        try {
            await RevokeApiKey(id_user, key.id);
            setForceUpdated((v) => v + 1);
        } catch (err: any) {
            setActionError(err?.response?.data?.error ?? "Erreur lors de la révocation.");
        }
    };

    const handleDelete = async (key: IApiKeyInfo) => {
        if (!window.confirm(`Supprimer définitivement la clé "${key.name}" ?`)) return;
        setActionError(null);
        try {
            await DeleteApiKey(id_user, key.id);
            setForceUpdated((v) => v + 1);
        } catch (err: any) {
            setActionError(err?.response?.data?.error ?? "Erreur lors de la suppression.");
        }
    };

    const handleCopyKey = () => {
        if (createdKey) {
            navigator.clipboard.writeText(createdKey.rawKey).then(() => {
                setCopiedRawKey(true);
                setTimeout(() => setCopiedRawKey(false), 2000);
            });
        }
    };

    const [activeSetupTab, setActiveSetupTab] = useState<"web" | "desktop" | "code">("web");

    const mcpEndpoint = window.location.origin.replace(":3000", ":4000") + "/api/mcp";

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return "—";
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="contentApiKeys">
            <div className="contentApiKeys__header">
                <div>
                    <h2 className="contentApiKeys__title">Clés API MCP</h2>
                    <p className="contentApiKeys__subtitle">
                        Gérez vos clés d'accès pour le serveur MCP FreeCompta. Utilisez ces clés dans vos outils IA
                        (Claude Desktop, Cursor, etc.) pour accéder à vos données.
                    </p>
                </div>
                <button
                    className="contentApiKeys__btnCreate"
                    onClick={() => {
                        setShowCreateForm(true);
                        setCreateError(null);
                    }}
                >
                    <span className="material-symbols-outlined">add</span>
                    Générer une clé
                </button>
            </div>

            {/* Info serveur MCP */}
            <div className="contentApiKeys__mcpInfo">
                <span className="material-symbols-outlined contentApiKeys__mcpInfoIcon">info</span>
                <div className="contentApiKeys__mcpInfoBody">
                    <div className="contentApiKeys__mcpInfoRow">
                        <strong>Endpoint MCP :</strong> <code>{mcpEndpoint}</code>
                        <span className="contentApiKeys__mcpInfoSep"> · </span>
                        <strong>Auth :</strong> <code>X-API-Key: &lt;votre clé&gt;</code>
                    </div>

                    {/* Onglets de configuration */}
                    <div className="contentApiKeys__setupTabs">
                        <button
                            className={`contentApiKeys__setupTab ${
                                activeSetupTab === "web" ? "contentApiKeys__setupTab--active" : ""
                            }`}
                            onClick={() => setActiveSetupTab("web")}
                        >
                            <span className="material-symbols-outlined">public</span>
                            Claude.ai
                        </button>
                        <button
                            className={`contentApiKeys__setupTab ${
                                activeSetupTab === "desktop" ? "contentApiKeys__setupTab--active" : ""
                            }`}
                            onClick={() => setActiveSetupTab("desktop")}
                        >
                            <span className="material-symbols-outlined">computer</span>
                            Claude Desktop
                        </button>
                        <button
                            className={`contentApiKeys__setupTab ${
                                activeSetupTab === "code" ? "contentApiKeys__setupTab--active" : ""
                            }`}
                            onClick={() => setActiveSetupTab("code")}
                        >
                            <span className="material-symbols-outlined">terminal</span>
                            Claude Code
                        </button>
                    </div>

                    {activeSetupTab === "web" && (
                        <div className="contentApiKeys__mcpSetup">
                            <span className="contentApiKeys__mcpSetupSub">
                                Approche officielle — fonctionne sur Claude.ai, Claude Desktop, mobile et Cowork. Le
                                serveur doit être <strong>accessible publiquement</strong> (les connexions proviennent
                                des serveurs Anthropic).
                            </span>
                            <ol className="contentApiKeys__mcpSteps">
                                <li>
                                    Aller sur{" "}
                                    <a
                                        href="https://claude.ai/customize/connectors"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        claude.ai/customize/connectors
                                    </a>
                                </li>
                                <li>
                                    Cliquer sur <code>+</code> puis <strong>« Add custom connector »</strong>
                                </li>
                                <li>Renseigner l'URL du serveur MCP :</li>
                            </ol>
                            <pre className="contentApiKeys__mcpCode">{mcpEndpoint}</pre>
                            <ol className="contentApiKeys__mcpSteps" start={4}>
                                <li>
                                    Cliquer sur <strong>« Advanced settings »</strong> et ajouter l'en-tête
                                    d'authentification :
                                </li>
                            </ol>
                            <pre className="contentApiKeys__mcpCode">{`X-API-Key: <votre-clé-api>`}</pre>
                            <span className="contentApiKeys__mcpSetupHint">
                                Pour les plans Team/Enterprise, un Owner doit d'abord ajouter le connecteur dans{" "}
                                <a
                                    href="https://claude.ai/admin-settings/connectors"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Organization settings → Connectors
                                </a>
                                .
                            </span>
                        </div>
                    )}

                    {activeSetupTab === "desktop" && (
                        <div className="contentApiKeys__mcpSetup">
                            <span className="contentApiKeys__mcpSetupSub">
                                Ajoutez ce bloc dans <code>claude_desktop_config.json</code> :
                            </span>
                            <pre className="contentApiKeys__mcpCode">{`{
  "mcpServers": {
    "freecompta": {
      "type": "http",
      "url": "${mcpEndpoint}",
      "headers": {
        "X-API-Key": "<votre-clé-api>"
      }
    }
  }
}`}</pre>
                            <span className="contentApiKeys__mcpSetupHint">
                                Chemin du fichier — <strong>macOS :</strong>{" "}
                                <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>
                                {"  ·  "}
                                <strong>Windows :</strong> <code>%APPDATA%\Claude\claude_desktop_config.json</code>
                            </span>
                        </div>
                    )}

                    {activeSetupTab === "code" && (
                        <div className="contentApiKeys__mcpSetup">
                            <span className="contentApiKeys__mcpSetupSub">
                                Exécutez cette commande dans votre terminal :
                            </span>
                            <pre className="contentApiKeys__mcpCode">{`claude mcp add --transport http freecompta ${mcpEndpoint} --header "X-API-Key: <votre-clé-api>"`}</pre>
                            <span className="contentApiKeys__mcpSetupSub">
                                Ou via <code>mcp-remote</code> (si votre version de Claude Code ne supporte pas encore
                                HTTP natif) :
                            </span>
                            <pre className="contentApiKeys__mcpCode">{`npx -y mcp-remote ${mcpEndpoint} --header "X-API-Key: <votre-clé-api>"`}</pre>
                            <span className="contentApiKeys__mcpSetupHint">
                                Pour l'approche <code>mcp-remote</code>, ajoutez le serveur dans Claude Code avec{" "}
                                <code>claude mcp add freecompta</code> en pointant vers cette commande npx.
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Formulaire de création */}
            {showCreateForm && (
                <div className="contentApiKeys__modal">
                    <div className="contentApiKeys__modalContent">
                        <h3>Nouvelle clé API MCP</h3>
                        <form onSubmit={handleCreate}>
                            <label className="contentApiKeys__label" htmlFor="apiKeyName">
                                Nom descriptif
                            </label>
                            <input
                                id="apiKeyName"
                                className="contentApiKeys__input"
                                type="text"
                                placeholder="ex : Claude Desktop, Cursor..."
                                value={newKeyName}
                                onChange={(e) => setNewKeyName(e.target.value)}
                                maxLength={255}
                                autoFocus
                            />
                            {createError && <p className="contentApiKeys__error">{createError}</p>}
                            <div className="contentApiKeys__modalActions">
                                <button
                                    type="button"
                                    className="contentApiKeys__btnCancel"
                                    onClick={() => {
                                        setShowCreateForm(false);
                                        setNewKeyName("");
                                        setCreateError(null);
                                    }}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="contentApiKeys__btnCreate"
                                    disabled={creatingKey || !newKeyName.trim()}
                                >
                                    {creatingKey ? "Génération…" : "Générer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal d'affichage de la clé créée */}
            {createdKey && (
                <div className="contentApiKeys__modal">
                    <div className="contentApiKeys__modalContent contentApiKeys__modalContent--key">
                        <span className="material-symbols-outlined contentApiKeys__keyIcon">key</span>
                        <h3>Clé API générée</h3>
                        <p className="contentApiKeys__keyWarning">
                            <strong>Copiez cette clé maintenant.</strong> Elle ne sera plus affichée après la fermeture
                            de cette fenêtre.
                        </p>
                        <div className="contentApiKeys__rawKeyBox">
                            <code className="contentApiKeys__rawKey">{createdKey.rawKey}</code>
                            <button className="contentApiKeys__btnCopy" onClick={handleCopyKey} title="Copier">
                                <span className="material-symbols-outlined">
                                    {copiedRawKey ? "check" : "content_copy"}
                                </span>
                            </button>
                        </div>
                        <button
                            className="contentApiKeys__btnCreate"
                            onClick={() => {
                                setCreatedKey(null);
                                setCopiedRawKey(false);
                            }}
                        >
                            J'ai copié ma clé
                        </button>
                    </div>
                </div>
            )}

            {actionError && <p className="contentApiKeys__error contentApiKeys__error--global">{actionError}</p>}

            {/* Liste des clés */}
            {loading && <FCSpinner size="middle" />}
            {error && <FCAlertMsgErrorAPI error={error} />}
            {keys && keys.length === 0 && (
                <div className="contentApiKeys__empty">
                    <span className="material-symbols-outlined contentApiKeys__emptyIcon">vpn_key_off</span>
                    <p>Aucune clé API. Générez votre première clé pour connecter un outil IA.</p>
                </div>
            )}
            {keys && keys.length > 0 && (
                <table className="contentApiKeys__table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Préfixe</th>
                            <th>État</th>
                            <th>Créée le</th>
                            <th>Dernière utilisation</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((key) => (
                            <tr key={key.id} className={!key.isActive ? "contentApiKeys__row--revoked" : ""}>
                                <td>{key.name}</td>
                                <td>
                                    <code>{key.keyPrefix}…</code>
                                </td>
                                <td>
                                    <span
                                        className={`contentApiKeys__badge ${
                                            key.isActive
                                                ? "contentApiKeys__badge--active"
                                                : "contentApiKeys__badge--revoked"
                                        }`}
                                    >
                                        {key.isActive ? "Active" : "Révoquée"}
                                    </span>
                                </td>
                                <td>{formatDate(key.createdAt)}</td>
                                <td>{formatDate(key.lastUsedAt)}</td>
                                <td className="contentApiKeys__actions">
                                    {key.isActive && (
                                        <button
                                            className="contentApiKeys__btnAction contentApiKeys__btnAction--revoke"
                                            onClick={() => handleRevoke(key)}
                                            title="Révoquer"
                                        >
                                            <span className="material-symbols-outlined">block</span>
                                        </button>
                                    )}
                                    <button
                                        className="contentApiKeys__btnAction contentApiKeys__btnAction--delete"
                                        onClick={() => handleDelete(key)}
                                        title="Supprimer"
                                    >
                                        <span className="material-symbols-outlined">delete</span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {/* Capacités MCP disponibles */}
            <div className="contentApiKeys__section">
                <h3 className="contentApiKeys__sectionTitle">Capacités MCP disponibles</h3>
                <p className="contentApiKeys__sectionSubtitle">
                    Ressources, outils et prompts accessibles par votre IA une fois connectée via une clé API.
                </p>
                {MCP_SECTIONS.map((section) => (
                    <div key={section.type} className="contentApiKeys__mcpGroup">
                        <div className="contentApiKeys__mcpGroupHeader">
                            <span className="material-symbols-outlined contentApiKeys__mcpGroupIcon">
                                {section.icon}
                            </span>
                            <span
                                className={`contentApiKeys__mcpGroupBadge contentApiKeys__mcpGroupBadge--${section.type}`}
                            >
                                {section.label}
                            </span>
                        </div>
                        <table className="contentApiKeys__table contentApiKeys__table--tools">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th>Paramètres</th>
                                </tr>
                            </thead>
                            <tbody>
                                {section.items.map((item) => (
                                    <tr key={item.name}>
                                        <td>
                                            <code>{item.name}</code>
                                        </td>
                                        <td>{item.description}</td>
                                        <td>
                                            {item.params.length === 0 ? (
                                                <span className="contentApiKeys__paramNone">—</span>
                                            ) : (
                                                item.params.map((p) => (
                                                    <span key={p.name} className="contentApiKeys__param">
                                                        <code>{p.name}</code>
                                                        {" — "}
                                                        {p.desc}
                                                        {p.required === false && <em> (optionnel)</em>}
                                                        {p.required === true && (
                                                            <em className="contentApiKeys__paramRequired"> *</em>
                                                        )}
                                                    </span>
                                                ))
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ContentApiKeys;
